import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { WhatsappLogo, Gear, FloppyDisk, PaperPlaneTilt, QrCode, CheckCircle, XCircle, WarningCircle, ArrowClockwise, Bell, BellSlash, ListBullets, Info, Link } from "@phosphor-icons/react";

export function WhatsAppConfigPanel() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState("");
  const [testMsg, setTestMsg] = useState("Hola, este es un mensaje de prueba desde MaxiTicket");
  const [sending, setSending] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp-config`, { headers });
      setConfig(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    setLoading(false);
  }, [token]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp-status`, { headers });
      setStatus(data);
    } catch { setStatus({ ok: false, status: "error" }); }
  }, [token]);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp-logs`, { headers });
      setLogs(data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { if (config?.gateway_url) loadStatus(); }, [config?.gateway_url, loadStatus]);

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/admin/whatsapp-config`, config, { headers });
      toast.success("Configuracion guardada");
      loadStatus();
    } catch { toast.error("Error al guardar"); }
  };

  const connectWA = async () => {
    if (!config.gateway_url || !config.gateway_url.trim()) {
      toast.error("Primero configura la URL del Gateway en la pestaña Configuracion");
      return;
    }
    if (!config.api_key || !config.api_key.trim()) {
      toast.error("Primero configura la API Key en la pestaña Configuracion");
      return;
    }
    try {
      setQrData(null);
      const { data } = await axios.post(`${API}/admin/whatsapp-connect`, {}, { headers });
      if (data.ok) {
        setQrData(data.data);
        toast.success("Escanea el codigo QR con tu WhatsApp");
      } else {
        toast.error(data.error || "Error al conectar con el gateway");
      }
    } catch (e) { toast.error("No se pudo conectar al servidor. Verifica la URL del gateway."); }
  };

  const sendTest = async () => {
    if (!config.gateway_url || !config.api_key) {
      toast.error("Primero configura el gateway en la pestaña Configuracion");
      return;
    }
    if (!config.enabled) {
      toast.error("Activa las notificaciones automaticas antes de enviar una prueba");
      return;
    }
    if (!testPhone) { toast.error("Ingresa un numero de WhatsApp"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/whatsapp-test`, { phone: testPhone, message: testMsg }, { headers });
      if (data.ok) toast.success("Mensaje enviado correctamente");
      else toast.error(data.error || "Error al enviar el mensaje");
    } catch { toast.error("No se pudo conectar al servidor de WhatsApp"); }
    setSending(false);
  };

  const statusLabel = {
    open: { text: "Conectado", color: "text-emerald-700 bg-emerald-50", icon: CheckCircle },
    close: { text: "Desconectado", color: "text-red-700 bg-red-50", icon: XCircle },
    connecting: { text: "Conectando...", color: "text-amber-700 bg-amber-50", icon: ArrowClockwise },
    not_configured: { text: "No configurado", color: "text-slate-500 bg-slate-100", icon: WarningCircle },
  };

  const getStatusInfo = () => {
    if (!status) return statusLabel.not_configured;
    const s = status.status?.toLowerCase() || "unknown";
    if (s === "open" || s === "connected") return statusLabel.open;
    if (s === "connecting") return statusLabel.connecting;
    if (s === "close" || s === "disconnected") return statusLabel.close;
    return { text: s, color: "text-slate-500 bg-slate-100", icon: WarningCircle };
  };

  if (loading || !config) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>;

  const sInfo = getStatusInfo();
  const StatusIcon = sInfo.icon;

  return (
    <div className="space-y-4" data-testid="whatsapp-config-panel">
      {/* Status bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <WhatsappLogo size={28} className="text-emerald-500" weight="fill" />
          <div>
            <h2 className="font-bold text-slate-800">WhatsApp</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusIcon size={14} className={sInfo.color.split(" ")[0]} weight="fill" />
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sInfo.color}`}>{sInfo.text}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadStatus()} className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs hover:bg-slate-200 flex items-center gap-1" data-testid="wa-refresh-btn">
            <ArrowClockwise size={14} /> Actualizar
          </button>
          <button onClick={connectWA} disabled={!config?.gateway_url || !config?.api_key}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="wa-connect-btn">
            <QrCode size={14} /> Conectar / QR
          </button>
        </div>
      </div>

      {/* QR Code */}
      {qrData && (
        <div className="bg-white rounded-xl border p-6 text-center space-y-3" data-testid="wa-qr-panel">
          <h3 className="font-semibold text-slate-700">Escanea este codigo QR con WhatsApp</h3>
          <p className="text-xs text-slate-500">Abre WhatsApp en tu telefono {'>'} Ajustes {'>'} Dispositivos vinculados {'>'} Vincular dispositivo</p>
          {qrData.base64 ? (
            <img src={`data:image/png;base64,${qrData.base64}`} alt="QR Code" className="mx-auto w-64 h-64 border rounded-xl" />
          ) : qrData.qrcode?.base64 ? (
            <img src={`data:image/png;base64,${qrData.qrcode.base64}`} alt="QR Code" className="mx-auto w-64 h-64 border rounded-xl" />
          ) : qrData.code ? (
            <div className="bg-slate-50 p-4 rounded-lg text-xs font-mono break-all">{qrData.code}</div>
          ) : (
            <p className="text-sm text-amber-600">No se genero codigo QR. Es posible que la instancia ya este conectada.</p>
          )}
          <button onClick={() => { loadStatus(); setQrData(null); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            Ya escanie, verificar conexion
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { id: "config", label: "Configuracion", icon: Gear },
          { id: "notifications", label: "Notificaciones", icon: Bell },
          { id: "test", label: "Prueba", icon: PaperPlaneTilt },
          { id: "logs", label: "Historial", icon: ListBullets },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === "logs") loadLogs(); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`wa-tab-${t.id}`}>
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {/* CONFIG TAB */}
      {tab === "config" && (
        <div className="bg-white rounded-xl border p-5 space-y-4 max-w-xl" data-testid="wa-config-form">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Gear size={18} /> Gateway WhatsApp</h3>
          <p className="text-xs text-slate-400">Configura la conexion con tu servidor de Evolution API o compatible</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">URL del Gateway *</label>
              <input value={config.gateway_url || ""} onChange={e => setConfig(p => ({ ...p, gateway_url: e.target.value }))}
                placeholder="https://tu-evolution-api.com" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="wa-gateway-url" />
              <p className="text-[10px] text-slate-400 mt-0.5">URL de tu instancia de Evolution API</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">API Key *</label>
              <input value={config.api_key || ""} onChange={e => setConfig(p => ({ ...p, api_key: e.target.value }))}
                type="password" placeholder="tu-api-key" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="wa-api-key" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Nombre de Instancia</label>
              <input value={config.instance_name || "maxiticket"} onChange={e => setConfig(p => ({ ...p, instance_name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="wa-instance" />
            </div>
            <div className="flex items-center gap-3 py-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={config.enabled || false} onChange={e => setConfig(p => ({ ...p, enabled: e.target.checked }))}
                  className="sr-only peer" data-testid="wa-enabled" />
                <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600" />
              </label>
              <span className="text-sm font-medium text-slate-700">Activar notificaciones automaticas</span>
            </div>
          </div>
          <button onClick={saveConfig} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5" data-testid="wa-save-config">
            <FloppyDisk size={16} /> Guardar Configuracion
          </button>

          {/* Setup Guide */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3" data-testid="wa-setup-guide">
            <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2"><Info size={16} weight="fill" /> Como configurar WhatsApp</h4>
            <div className="text-xs text-blue-700 space-y-2">
              <p>Para enviar notificaciones por WhatsApp necesitas un <strong>servidor de Evolution API</strong> (gateway). Opciones:</p>
              <div className="space-y-1.5 ml-1">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-blue-600 mt-0.5">1.</span>
                  <div>
                    <strong>Evolution API (Recomendado):</strong> Servidor gratuito y open-source.
                    <br />
                    <a href="https://doc.evolution-api.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline flex items-center gap-1 mt-0.5">
                      <Link size={12} /> doc.evolution-api.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-blue-600 mt-0.5">2.</span>
                  <div>
                    <strong>Hosting en la nube:</strong> Puedes desplegarlo en un VPS (DigitalOcean, AWS, etc.) con Docker.
                  </div>
                </div>
              </div>
              <div className="bg-blue-100/60 rounded-lg p-2.5 mt-2">
                <p className="font-semibold text-blue-800">Pasos rapidos:</p>
                <ol className="list-decimal list-inside space-y-0.5 mt-1 text-blue-700">
                  <li>Instala Evolution API en tu servidor</li>
                  <li>Copia la <strong>URL</strong> y la <strong>API Key</strong> que te proporciona</li>
                  <li>Pegalos en los campos de arriba y guarda</li>
                  <li>Haz clic en <strong>"Conectar / QR"</strong> y escanea con tu celular</li>
                  <li>Activa las notificaciones y envia un mensaje de prueba</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATIONS TAB */}
      {tab === "notifications" && (
        <div className="bg-white rounded-xl border p-5 space-y-4 max-w-xl" data-testid="wa-notifications">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Bell size={18} /> Notificaciones Automaticas</h3>
          <p className="text-xs text-slate-400">Selecciona que eventos activan un mensaje de WhatsApp</p>
          <div className="space-y-3">
            {[
              { key: "notify_ticket_created", label: "Ticket creado", desc: "Notifica al cliente cuando se crea su ticket" },
              { key: "notify_ticket_assigned", label: "Ticket asignado", desc: "Notifica al ingeniero cuando se le asigna un ticket" },
              { key: "notify_status_changed", label: "Cambio de estatus", desc: "Notifica al cliente cuando cambia el estatus del ticket" },
              { key: "notify_urgent", label: "Ticket urgente", desc: "Notifica a los admins cuando se crea un ticket urgente/critico" },
            ].map(n => (
              <div key={n.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-700">{n.label}</p>
                  <p className="text-[11px] text-slate-400">{n.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={config[n.key] || false} onChange={e => setConfig(p => ({ ...p, [n.key]: e.target.checked }))}
                    className="sr-only peer" data-testid={`wa-${n.key}`} />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600" />
                </label>
              </div>
            ))}
          </div>
          <button onClick={saveConfig} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5">
            <FloppyDisk size={16} /> Guardar
          </button>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-700"><strong>Requisito:</strong> Los usuarios deben tener un numero de WhatsApp registrado en su perfil para recibir notificaciones.</p>
          </div>
        </div>
      )}

      {/* TEST TAB */}
      {tab === "test" && (
        <div className="bg-white rounded-xl border p-5 space-y-4 max-w-xl" data-testid="wa-test">
          <h3 className="font-semibold text-slate-700 flex items-center gap-2"><PaperPlaneTilt size={18} /> Enviar Mensaje de Prueba</h3>
          {(!config.gateway_url || !config.api_key) ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 flex items-center gap-2"><WarningCircle size={16} weight="fill" /> <strong>Gateway no configurado</strong></p>
              <p className="text-xs text-amber-600 mt-1">Ve a la pestaña "Configuracion" para ingresar la URL del gateway y la API Key antes de enviar mensajes de prueba.</p>
            </div>
          ) : !config.enabled ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 flex items-center gap-2"><WarningCircle size={16} weight="fill" /> <strong>Notificaciones desactivadas</strong></p>
              <p className="text-xs text-amber-600 mt-1">Activa las notificaciones automaticas en la pestaña "Configuracion" para poder enviar mensajes.</p>
            </div>
          ) : null}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Numero de WhatsApp *</label>
              <input value={testPhone} onChange={e => setTestPhone(e.target.value)}
                placeholder="+52 1234567890" className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="wa-test-phone" />
              <p className="text-[10px] text-slate-400 mt-0.5">Formato: 10 digitos o con codigo de pais (ej: 5212345678)</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Mensaje</label>
              <textarea value={testMsg} onChange={e => setTestMsg(e.target.value)}
                rows={3} className="w-full px-3 py-2 border rounded-lg text-sm mt-0.5" data-testid="wa-test-msg" />
            </div>
          </div>
          <button onClick={sendTest} disabled={sending || !config.gateway_url || !config.api_key || !config.enabled}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="wa-send-test">
            <PaperPlaneTilt size={16} /> {sending ? "Enviando..." : "Enviar Prueba"}
          </button>
        </div>
      )}

      {/* LOGS TAB */}
      {tab === "logs" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-700">Historial de Notificaciones</h3>
            <button onClick={loadLogs} className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs hover:bg-slate-200 flex items-center gap-1">
              <ArrowClockwise size={14} /> Recargar
            </button>
          </div>
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Fecha</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Evento</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Ticket</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Destino</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Telefono</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map(l => (
                  <tr key={l.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 text-xs text-slate-500">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{l.event_type}</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{l.ticket_number}</td>
                    <td className="px-3 py-2.5 text-xs">{l.target_name} ({l.target})</td>
                    <td className="px-3 py-2.5 text-xs font-mono">{l.phone}</td>
                    <td className="px-3 py-2.5 text-center">
                      {l.success
                        ? <CheckCircle size={16} className="inline text-emerald-500" weight="fill" />
                        : <span className="text-[10px] text-red-500">{l.error?.slice(0, 30)}</span>
                      }
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin notificaciones enviadas</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
